import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '../../../services/supabaseClient';
import { useAuth } from '../../../context/AuthContext';
import RexiPopup from '../../../components/RexiPopup';
import RexiTooltip from '../../../components/RexiTooltip';
import SmartPagination from '../../../components/SmartPagination';
import '../DataTableTemplate.css';
import './TestLabs.css';
import AddEditLabModal from './AddEditLabModal';

interface TestLab {
  id: number;
  name: string;
  address_line_1?: string;
  address_line_2?: string;
  suite?: string;
  city?: string;
  state_province?: string;
  country?: string;
  postal_code?: string;
  telephone?: string;
  email?: string;
  cpsc_id?: string;
  status: 'active' | 'inactive';
  source?: 'user' | 'certified'; // Indicates if this is user's lab or certified lab
  auth_id?: string; // Only for user labs
}

// Moved outside component to prevent recreation on every render
const FilterInput = ({ label, placeholder, column, value, onChange }: {
  label: string;
  placeholder: string;
  column: string;
  value: string;
  onChange: (column: string, value: string) => void;
}) => (
  <div className="filter-group">
    <label className="filter-label">{label}</label>
    <input
      type="text"
      className="filter-input"
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(column, e.target.value)}
    />
  </div>
);

const TestLabs = () => {
  const { user } = useAuth();
  const [testLabs, setTestLabs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [columnFilters, setColumnFilters] = useState({
    cpsc_id: '',
    name: '',
    address: '',
    city: '',
    state_province: '',
    country: '',
    postal_code: '',
    phone: '',
    email: '',
    status: 'all'
  });
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [popupState, setPopupState] = useState({
    help: false,
    delete: false,
    modal: false,
    foreignKeyError: false
  });
  const [labToDelete, setLabToDelete] = useState(null);
  const [hoveredDisabledButton, setHoveredDisabledButton] = useState(null);

  const [editingLab, setEditingLab] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [formData, setFormData] = useState({
    name: '',
    address_line_1: '',
    address_line_2: '',
    suite: '',
    city: '',
    state_province: '',
    country: '',
    postal_code: '',
    telephone: '',
    email: '',
    cpsc_id: '',
    status: 'active' as 'active' | 'inactive'
  });
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // Fetch test labs from both user and certified tables
  const fetchTestLabs = async () => {
    try {
      setLoading(true);
      
      // Fetch user's test labs
      const { data: userLabs, error: userError } = await supabase
        .from('test_labs')
        .select('*')
        .eq('auth_id', user?.id);

      // Fetch certified labs
      const { data: certifiedLabs, error: certifiedError } = await supabase
        .from('certified_labs')
        .select('*');

      if (userError) throw userError;
      if (certifiedError) throw certifiedError;

      // Combine and mark source
      const combinedLabs: TestLab[] = [
        ...(userLabs || []).map(lab => ({ ...lab, source: 'user' as const })),
        ...(certifiedLabs || []).map(lab => ({ ...lab, source: 'certified' as const }))
      ];

      // Sort by CPSC ID
      combinedLabs.sort((a, b) => (a.cpsc_id || '').localeCompare(b.cpsc_id || ''));

      setTestLabs(combinedLabs);
    } catch (err: any) {
      setError(err.message);
      console.error('Error fetching test labs:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.id) {
      fetchTestLabs();
    }
  }, [user?.id]);

  // Helper functions for popup management
  const togglePopup = (popup: keyof typeof popupState, show: boolean) => {
    setPopupState((prev: typeof popupState) => ({ ...prev, [popup]: show }));
  };

  // Table configuration - drives both headers and data rendering
  const tableColumns = [
    { key: 'cpsc_id', label: 'CPSC ID', sortable: true, className: 'cpsc', showInTable: true },
    { key: 'name', label: 'Lab Name', sortable: false, className: 'name', showInTable: true },
    { key: 'address_line_1', label: 'Address Line 1', sortable: false, showInTable: false }, // Hidden but available for filtering
    { key: 'address_line_2', label: 'Address Line 2', sortable: false, showInTable: false }, // Hidden but available for filtering
    { key: 'suite', label: 'Suite', sortable: false, showInTable: false }, // Only for CSV
    { key: 'city', label: 'City', sortable: false, className: 'city', showInTable: true },
    { key: 'state_province', label: 'State/Province', sortable: false, className: 'state', showInTable: true },
    { key: 'country', label: 'Country', sortable: true, className: 'country', showInTable: true },
    { key: 'postal_code', label: 'Postal Code', sortable: false, className: 'postal', showInTable: false }, // Hidden but available for filtering
    { key: 'telephone', label: 'Phone', sortable: false, className: 'phone', showInTable: false }, // Hidden but available for filtering
    { key: 'email', label: 'Email', sortable: false, className: 'email', showInTable: false }, // Hidden but available for filtering
    { key: 'status', label: 'Status', sortable: true, className: 'status', showInTable: true },
    { key: 'actions', label: 'Actions', sortable: false, className: 'actions', showInTable: true }
  ];

  // Filter test labs based on column filters
  const filteredTestLabs = testLabs.filter((lab: TestLab) => {
    const matchesCpscId = !columnFilters.cpsc_id || 
      lab.cpsc_id?.toLowerCase().includes(columnFilters.cpsc_id.toLowerCase());
    
    const matchesName = !columnFilters.name || 
      lab.name?.toLowerCase().includes(columnFilters.name.toLowerCase());
    
    const matchesAddress = !columnFilters.address || 
      (lab.address_line_1?.toLowerCase().includes(columnFilters.address.toLowerCase()) ||
       lab.address_line_2?.toLowerCase().includes(columnFilters.address.toLowerCase()));
    
    const matchesCity = !columnFilters.city || 
      lab.city?.toLowerCase().includes(columnFilters.city.toLowerCase());
    
    const matchesStateProvince = !columnFilters.state_province || 
      lab.state_province?.toLowerCase().includes(columnFilters.state_province.toLowerCase());
    
    const matchesCountry = !columnFilters.country || 
      lab.country?.toLowerCase().includes(columnFilters.country.toLowerCase());
    
    const matchesPostalCode = !columnFilters.postal_code || 
      lab.postal_code?.toLowerCase().includes(columnFilters.postal_code.toLowerCase());
    
    const matchesPhone = !columnFilters.phone || 
      lab.telephone?.toLowerCase().includes(columnFilters.phone.toLowerCase());
    
    const matchesEmail = !columnFilters.email || 
      lab.email?.toLowerCase().includes(columnFilters.email.toLowerCase());
    
    const matchesStatus = columnFilters.status === 'all' || lab.status === columnFilters.status;
    
    return matchesCpscId && matchesName && matchesAddress && matchesCity && matchesStateProvince && 
           matchesCountry && matchesPostalCode && matchesPhone && matchesEmail && 
           matchesStatus;
  });

  const handleColumnFilterChange = (column: string, value: string) => {
    setColumnFilters((prev: typeof columnFilters) => ({ ...prev, [column]: value }));
    setCurrentPage(1); // Reset to first page when filters change
  };

  const clearAllFilters = () => {
    setColumnFilters({
      cpsc_id: '',
      name: '',
      address: '',
      city: '',
      state_province: '',
      country: '',
      postal_code: '',
      phone: '',
      email: '',
      status: 'all'
    });
    setCurrentPage(1); // Reset to first page when clearing filters
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Reusable interactive cell component
  const InteractiveCell = ({ children, className = "", dataLabel = "" }: any) => (
    <div className={`table-cell ${className}`} data-label={dataLabel}>
      <div
        className="table-cell-input"
        contentEditable
        suppressContentEditableWarning={true}
        onInput={(e: any) => e.preventDefault()}
        onKeyDown={(e: any) => {
          const allowedKeys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End', 'Tab', 'Escape'];
          const isCopyOperation = e.ctrlKey && (e.key === 'c' || e.key === 'a');
          if (!allowedKeys.includes(e.key) && !isCopyOperation) {
            e.preventDefault();
          }
        }}
        onPaste={(e: any) => e.preventDefault()}
        onMouseDown={(e: any) => e.stopPropagation()}
        onBlur={(e: any) => e.currentTarget.scrollLeft = 0}
      >
        {children}
      </div>
    </div>
  );

  // Reusable table header cell component
  const TableHeaderCell = ({ column }: any) => (
    <div className={`table-cell ${column.className || ''}`}>
      {column.sortable ? (
        <div className="sortable-header" onClick={() => handleSort(column.key)}>
          {column.label}
          <span className={`sort-icon ${getSortIcon(column.key)} ${sortConfig.key === column.key ? 'active' : ''}`}></span>
        </div>
      ) : (
        column.label
      )}
    </div>
  );

  // Reusable table data cell component
  const TableDataCell = ({ column, lab }: any) => {
    if (column.key === 'actions') {
      return (
        <div className="table-cell" data-label="Actions">
          <div className="table-actions">
            <button className="table-action-btn table-edit-btn" onClick={() => openEditModal(lab)}>
              Details
            </button>
            {/* Always show delete button, but disable for certified labs */}
            {lab.source === 'certified' ? (
              <div
                onMouseEnter={() => setHoveredDisabledButton(lab.id)}
                onMouseLeave={() => setHoveredDisabledButton(null)}
              >
                <RexiTooltip
                  message="CPSC-certified labs can't be deleted from this table."
                  visible={hoveredDisabledButton === lab.id}
                  position="top"
                >
                  <button 
                    className="table-action-btn table-delete-btn-disabled"
                    disabled={true}
                  >
                    Delete
                  </button>
                </RexiTooltip>
              </div>
            ) : (
              <button 
                className="table-action-btn table-delete-btn"
                onClick={() => confirmDelete(lab)}
              >
                Delete
              </button>
            )}
          </div>
        </div>
      );
    }


    if (column.key === 'status') {
      return (
        <div className="table-cell status" data-label="Status">
          <span className={`status-${lab.status?.toLowerCase()}`}>
            {lab.status?.toLowerCase() === 'active' ? 'Active' : 'Inactive'}
          </span>
        </div>
      );
    }

    // Regular interactive cell
    return (
      <InteractiveCell className={column.className || ''} dataLabel={column.label}>
        {(lab as any)[column.key] || '-'}
      </InteractiveCell>
    );
  };

  const hasActiveFilters = Object.values(columnFilters).some(filter => filter && filter !== 'all');

  // Sorting functionality
  const handleSort = (key: string) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Apply sorting to filtered results
  const sortedTestLabs = useMemo(() => {
    if (!sortConfig.key) return filteredTestLabs;

    return [...filteredTestLabs].sort((a, b) => {
      const aValue = (a[sortConfig.key] || '').toString().toLowerCase();
      const bValue = (b[sortConfig.key] || '').toString().toLowerCase();

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [filteredTestLabs, sortConfig]);

  // Apply pagination to sorted results
  const paginatedTestLabs = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return sortedTestLabs.slice(startIndex, endIndex);
  }, [sortedTestLabs, currentPage, itemsPerPage]);

  // Calculate pagination info
  const totalPages = Math.ceil(sortedTestLabs.length / itemsPerPage);
  const totalItems = sortedTestLabs.length;

  const getSortIcon = (columnKey: string) => {
    if (sortConfig.key !== columnKey) {
      return 'none';
    }
    return sortConfig.direction;
  };

  // Handle delete test lab
  const handleDeleteTestLab = async () => {
    if (!labToDelete || labToDelete.source === 'certified') return;

    try {
      const { error } = await supabase
        .from('test_labs')
        .delete()
        .eq('id', labToDelete.id)
        .eq('auth_id', user?.id);

      if (error) {
        // Check if it's a foreign key constraint error
        if (error.message.includes('violates foreign key constraint') && 
            error.message.includes('test_reports_other_lab_id_fkey')) {
          // Close delete confirmation popup
          togglePopup('delete', false);
          setLabToDelete(null);
          // Show custom foreign key error popup
          togglePopup('foreignKeyError', true);
          return;
        }
        throw error;
      }

      // Refetch all data to ensure consistency
      await fetchTestLabs();
      togglePopup('delete', false);
      setLabToDelete(null);
    } catch (err: any) {
      setError(err.message);
      console.error('Error deleting test lab:', err);
    }
  };

  const confirmDelete = (lab: TestLab) => {
    setLabToDelete(lab);
    togglePopup('delete', true);
  };

  // Modal functions
  const openAddModal = () => {
    setEditingLab(null);
    setFormData({
      name: '',
      address_line_1: '',
      address_line_2: '',
      suite: '',
      city: '',
      state_province: '',
      country: '',
      postal_code: '',
      telephone: '',
      email: '',
      cpsc_id: '',
      status: 'active'
    });
    setFormErrors({});
    togglePopup('modal', true);
  };

  const openEditModal = (lab: TestLab) => {
    setEditingLab(lab);
    setFormData({
      name: lab.name || '',
      address_line_1: lab.address_line_1 || '',
      address_line_2: lab.address_line_2 || '',
      suite: lab.suite || '',
      city: lab.city || '',
      state_province: lab.state_province || '',
      country: lab.country || '',
      postal_code: lab.postal_code || '',
      telephone: lab.telephone || '',
      email: lab.email || '',
      cpsc_id: lab.cpsc_id || '',
      status: lab.status || 'active'
    });
    setFormErrors({});
    togglePopup('modal', true);
  };

  const closeModal = () => {
    togglePopup('modal', false);
    setEditingLab(null);
    setFormErrors({});
    setSaving(false);
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev: typeof formData) => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors((prev: Record<string, string>) => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};
    
    // Required fields validation
    const requiredFields = [
      { field: 'name', label: 'Lab name' }
    ];
    
    requiredFields.forEach(({ field, label }) => {
      if (!formData[field]?.trim()) {
        errors[field] = `${label} is required`;
      }
    });
    
    // Email validation
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveTestLab = async () => {
    if (!validateForm()) return;
    
    setSaving(true);
    try {
      const labData = {
        ...formData,
        auth_id: user?.id
      };

      if (editingLab) {
        // Update existing test lab
        const { error } = await supabase
          .from('test_labs')
          .update(labData)
          .eq('id', editingLab.id)
          .eq('auth_id', user?.id);

        if (error) throw error;

        setTestLabs(testLabs.map((l: TestLab) => 
          l.id === editingLab.id 
            ? { ...l, ...labData, source: l.source }
            : l
        ));
      } else {
        // Create new test lab
        const { data, error } = await supabase
          .from('test_labs')
          .insert([labData])
          .select()
          .single();

        if (error) throw error;

        // Refetch all data to ensure consistency
        await fetchTestLabs();
      }

      closeModal();
    } catch (err: any) {
      setError(err.message);
      console.error('Error saving test lab:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleExportCSV = () => {
    // Use filtered test labs (what's currently shown on page)
    const dataToExport = sortedTestLabs;
    
    if (dataToExport.length === 0) {
      alert('No data to export');
      return;
    }

    // Define headers from table configuration (excluding actions)
    const csvColumns = tableColumns.filter(col => col.key !== 'actions');
    const headers = csvColumns.map(col => col.label);

    // Convert data to CSV format using table configuration
    const csvContent = [
      headers.join(','),
      ...dataToExport.map((lab: TestLab) => 
        csvColumns.map(col => `"${(lab as any)[col.key] || ''}"`)
          .join(',')
      )
    ].join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `test_labs_export_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <div className="data-table-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="data-table-container">
      <h1 className="data-table-title">Test Labs</h1>
      <div className="page-help-link" onClick={() => togglePopup('help', true)}>
        Ask Rexi about this page
      </div>

      {error && (
        <div className="error-state">
          <strong>Error:</strong> {error}
        </div>
      )}

      <div className="test-labs-table-container">
        {/* Filter Toggle and Card */}
        <div className="test-labs-filter-section">
        <div className="filter-section">
        <div className="filter-toggle-header">
          <button 
            className="filter-toggle-btn"
            onClick={() => setShowFilters(!showFilters)}
          >
            {showFilters ? '▼' : '▶'} Filter Test Labs
            {hasActiveFilters && <span className="active-filter-indicator">({Object.values(columnFilters).filter(filter => filter && filter !== 'all').length} active)</span>}
          </button>
          {hasActiveFilters && (
            <button className="clear-filters-btn" onClick={clearAllFilters}>
              Clear All Filters
            </button>
          )}
        </div>
        
        {showFilters && (
          <div className="table-filters">
            <div className="filters-grid">
              <FilterInput 
                label="CPSC ID" 
                placeholder="Search by CPSC ID..." 
                column="cpsc_id" 
                value={columnFilters.cpsc_id}
                onChange={handleColumnFilterChange}
              />
              <FilterInput 
                label="Lab Name" 
                placeholder="Search by lab name..." 
                column="name" 
                value={columnFilters.name}
                onChange={handleColumnFilterChange}
              />
              <FilterInput 
                label="Address" 
                placeholder="Search by address..." 
                column="address" 
                value={columnFilters.address}
                onChange={handleColumnFilterChange}
              />
              <FilterInput 
                label="City" 
                placeholder="Search by city..." 
                column="city" 
                value={columnFilters.city}
                onChange={handleColumnFilterChange}
              />
              <FilterInput 
                label="State/Province" 
                placeholder="Search by state/province..." 
                column="state_province" 
                value={columnFilters.state_province}
                onChange={handleColumnFilterChange}
              />
              <FilterInput 
                label="Country" 
                placeholder="Search by country..." 
                column="country" 
                value={columnFilters.country}
                onChange={handleColumnFilterChange}
              />
              <FilterInput 
                label="Postal Code" 
                placeholder="Search by postal code..." 
                column="postal_code" 
                value={columnFilters.postal_code}
                onChange={handleColumnFilterChange}
              />
              <FilterInput 
                label="Phone" 
                placeholder="Search by phone..." 
                column="phone" 
                value={columnFilters.phone}
                onChange={handleColumnFilterChange}
              />
              <FilterInput 
                label="Email" 
                placeholder="Search by email..." 
                column="email" 
                value={columnFilters.email}
                onChange={handleColumnFilterChange}
              />
              <div className="filter-group">
                <label className="filter-label">Status</label>
                <select
                  className="filter-select"
                  value={columnFilters.status}
                  onChange={(e) => handleColumnFilterChange('status', e.target.value)}
                >
                  <option value="all">All Statuses</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>
          </div>
        )}
        </div>
      </div>

      {/* Table Controls */}
      <div className="test-labs-table-header-controls">
        <button className="add-data-btn" onClick={openAddModal}>
          + Add Test Lab
        </button>
        <button className="export-csv-btn" onClick={handleExportCSV}>
          Export CSV
        </button>
      </div>

      {/* Test Labs Table */}
      <div className="test-labs-table-wrapper">
        <div className="test-labs-table-scrollable">
          <div className="test-labs-data-table">
          <div className="table-header">
            <div className="test-labs-table-row">
              {tableColumns.filter(col => col.showInTable).map((column) => (
                <TableHeaderCell key={column.key} column={column} />
              ))}
            </div>
          </div>
          
          {sortedTestLabs.length === 0 ? (
            <div className="test-labs-table-row">
              <div className="empty-table-message">
                {hasActiveFilters ? (
                  <>
                    <h4>No test labs found</h4>
                    <p>Try adjusting your filter criteria.</p>
                  </>
                ) : (
                  <p style={{color: '#999', fontStyle: 'italic'}}>No test labs yet</p>
                )}
              </div>
            </div>
          ) : (
            paginatedTestLabs.map((lab: TestLab) => (
              <div key={lab.id} className="test-labs-table-row">
                {tableColumns.filter(col => col.showInTable).map((column) => (
                  <TableDataCell key={column.key} column={column} lab={lab} />
                ))}
              </div>
            ))
          )}
          </div>
        </div>
      </div>

      {/* Pagination */}
      <SmartPagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={totalItems}
        itemsPerPage={itemsPerPage}
        itemType="test labs"
        onPageChange={handlePageChange}
      />
      </div>

      {/* Help Popup */}
      {popupState.help && (
        <RexiPopup
          message="Manage your test labs here. Test labs are the facilities that perform product testing for certification purposes. For children's products, CPSC certified labs must be used, you will see a CPSC ID on this page for certified labs. For non-children's products, you can add and edit the lab data for any non-certified labs you may use."
          onClose={() => togglePopup('help', false)}
          confirmText="Got It"
        />
      )}

      {/* Delete Confirmation Popup */}
      {popupState.delete && labToDelete && (
        <RexiPopup
          message="Are you sure you want to delete this test lab? This action cannot be undone."
          onClose={() => {
            togglePopup('delete', false);
            setLabToDelete(null);
          }}
          onContinue={handleDeleteTestLab}
          showCancel={true}
        />
      )}

      {/* Foreign Key Constraint Error Popup */}
      {popupState.foreignKeyError && (
        <RexiPopup
          message="This test lab cannot be deleted because it is currently being used in one or more test reports. To delete this test lab, you must first remove or reassign all test reports that reference it."
          onClose={() => togglePopup('foreignKeyError', false)}
          confirmText="Understood"
        />
      )}

{/*
      >>>>>>>>>>>>>> Add/Edit Test Lab Modal
      {popupState.modal && (
        <div className="data-modal-overlay" onClick={closeModal}>
          <div className="data-modal" onClick={(e) => e.stopPropagation()}>
            <div className="data-modal-header">
              <h2 className="data-modal-title">
                {editingLab ? 'Test Lab Details' : 'Add New Test Lab'}
              </h2>
              <button className="modal-close-btn" onClick={closeModal}>
                ×
              </button>
            </div>

            <div className="data-modal-body">
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">
                    Lab Name <span className="required-indicator">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Enter lab name"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    readOnly={editingLab?.source === 'certified'}
                  />
                  {formErrors.name && <div className="form-error">{formErrors.name}</div>}
                </div>

                <div className="form-group">
                  <label className="form-label">Address Line 1</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Enter address line 1"
                    value={formData.address_line_1}
                    onChange={(e) => handleInputChange('address_line_1', e.target.value)}
                    readOnly={editingLab?.source === 'certified'}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Address Line 2</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Enter address line 2"
                    value={formData.address_line_2}
                    onChange={(e) => handleInputChange('address_line_2', e.target.value)}
                    readOnly={editingLab?.source === 'certified'}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Suite</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Enter suite number"
                    value={formData.suite}
                    onChange={(e) => handleInputChange('suite', e.target.value)}
                    readOnly={editingLab?.source === 'certified'}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">City</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Enter city"
                    value={formData.city}
                    onChange={(e) => handleInputChange('city', e.target.value)}
                    readOnly={editingLab?.source === 'certified'}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">State/Province</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Enter state or province"
                    value={formData.state_province}
                    onChange={(e) => handleInputChange('state_province', e.target.value)}
                    readOnly={editingLab?.source === 'certified'}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Country</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Enter country"
                    value={formData.country}
                    onChange={(e) => handleInputChange('country', e.target.value)}
                    readOnly={editingLab?.source === 'certified'}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Postal Code</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Enter postal code"
                    value={formData.postal_code}
                    onChange={(e) => handleInputChange('postal_code', e.target.value)}
                    readOnly={editingLab?.source === 'certified'}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Phone</label>
                  <input
                    type="tel"
                    className="form-input"
                    placeholder="Enter phone number"
                    value={formData.telephone}
                    onChange={(e) => handleInputChange('telephone', e.target.value)}
                    readOnly={editingLab?.source === 'certified'}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input
                    type="email"
                    className="form-input"
                    placeholder="Enter email address"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    readOnly={editingLab?.source === 'certified'}
                  />
                  {formErrors.email && <div className="form-error">{formErrors.email}</div>}
                </div>

                <div className="form-group">
                  <label className="form-label">CPSC ID</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Enter CPSC ID (optional)"
                    value={formData.cpsc_id}
                    onChange={(e) => handleInputChange('cpsc_id', e.target.value)}
                    readOnly={editingLab?.source === 'certified'}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Status</label>
                  <select
                    className="form-select"
                    value={formData.status}
                    onChange={(e) => handleInputChange('status', e.target.value)}
                    disabled={editingLab?.source === 'certified'}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="data-modal-footer">
              <button 
                className="modal-btn modal-btn-cancel" 
                onClick={closeModal}
                disabled={saving}
              >
                {editingLab?.source === 'certified' ? 'Close' : 'Cancel'}
              </button>
              >>>>>>>>>>> Only show save button for user labs or new labs
              {editingLab?.source !== 'certified' && (
                <button 
                  className="modal-btn modal-btn-save" 
                  onClick={handleSaveTestLab}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : (editingLab ? 'Update Test Lab' : 'Add Test Lab')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
*/}



      <AddEditLabModal
        isOpen={popupState.modal}
        onClose={closeModal}
        editingLab={editingLab}
        formData={formData}
        formErrors={formErrors}
        saving={saving}
        handleInputChange={handleInputChange}
        handleSaveTestLab={handleSaveTestLab}
      />



    </div>
  );
};

export default TestLabs;